import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminService, CollectionConfig, CollectionField } from './admin.service';
import { ConfirmDialogComponent } from './confirm-dialog.component';

export interface RowDialogData {
    config: CollectionConfig;
    row: Record<string, unknown> | null;
}

export interface RowDialogResult {
    save?: Record<string, unknown>;
    delete?: boolean;
}

@Component({
    selector: 'app-row-dialog',
    templateUrl: './row-dialog.component.html',
    styleUrls: ['./row-dialog.component.scss'],
    standalone: false
})
export class RowDialogComponent {
    form = new FormGroup({});
    editing = false;
    uploadingField: string | null = null;
    private imageValues: Record<string, string | null> = {};

    constructor(
        public dialogRef: MatDialogRef<RowDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: RowDialogData,
        private admin: AdminService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar
    ) {
        this.editing = !!data.row;
        for (const field of data.config.fields) {
            if (field.type === 'image') continue;
            let current: string | number | null;
            if (field.type === 'password') {
                current = '';
            } else if (this.editing) {
                current = (this.data.row![field.name] as string | number | null) ?? this.initialValue(field);
            } else {
                current = field.default ?? this.initialValue(field);
            }
            const required = field.type === 'password' ? !this.editing : !!field.required;
            this.form.addControl(field.name, new FormControl(current, required ? Validators.required : []));
        }
    }

    private initialValue(field: CollectionField): string | number | null {
        switch (field.type) {
            case 'number': return null;
            case 'select': return field.default ?? null;
            case 'date': return '';
            default: return '';
        }
    }

    preview(fileId: string | null) {
        return this.admin.preview(fileId);
    }

    fileId(field: CollectionField): string | null {
        if (field.name in this.imageValues) {
            return this.imageValues[field.name];
        }
        return typeof this.data.row?.[field.name] === 'string' ? String(this.data.row[field.name]) : null;
    }

    hasMissingRequiredImage(): boolean {
        return this.data.config.fields.some(
            field => field.type === 'image' && field.required && !this.fileId(field)
        );
    }

    onFileSelected(field: CollectionField, event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            this.snackBar.open('El archivo debe ser una imagen.', 'Cerrar', { duration: 4000 });
            return;
        }
        this.uploadingField = field.name;
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = String(reader.result).split(',')[1];
            this.admin.upload(file.type, base64, file.name).subscribe({
                next: uploaded => {
                    this.imageValues[field.name] = uploaded.id;
                    if (this.data.row) {
                        this.data.row![field.name] = uploaded.id;
                    }
                    this.uploadingField = null;
                    this.snackBar.open('Imagen subida.', '', { duration: 2500 });
                },
                error: error => {
                    this.uploadingField = null;
                    this.snackBar.open(error.message, 'Cerrar', { duration: 5000 });
                }
            });
        };
        reader.readAsDataURL(file);
    }

    clearImage(field: CollectionField): void {
        this.imageValues[field.name] = null;
        if (this.data.row) {
            this.data.row![field.name] = null;
        }
    }

    onSave(): void {
        if (this.form.invalid || this.hasMissingRequiredImage()) return;
        const record: Record<string, unknown> = {};
        for (const field of this.data.config.fields) {
            if (field.type === 'image') {
                record[field.name] = this.fileId(field);
                continue;
            }
            if (field.type === 'password' && !this.form.controls[field.name].value) {
                continue;
            }
            let value: string | number | null = this.form.controls[field.name].value;
            if (value === '') value = null;
            record[field.name] = value;
        }
        this.dialogRef.close({ save: record });
    }

    onDelete(): void {
        if (!this.editing) return;
        const confirmRef = this.dialog.open(ConfirmDialogComponent, {
            width: '400px',
            maxWidth: '95vw',
            autoFocus: 'dialog',
            data: {
                title: 'Eliminar registro',
                message: '¿Seguro que quieres eliminar este registro? Esta acción no se puede deshacer.',
                confirmLabel: 'Eliminar',
                danger: true
            }
        });
        confirmRef.afterClosed().subscribe(confirmed => {
            if (confirmed) this.dialogRef.close({ delete: true });
        });
    }

    onCancel(): void {
        this.dialogRef.close();
    }
}